import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  Refresh,
  Add,
  CheckCircle,
  Error,
  Warning,
  Schedule,
  PlayArrow,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axiosClient from '../api/axiosClient';
import OperationDetailDialog from '../components/OperationDetailDialog';

interface FileOperation {
  id: number;
  directoryPath: string;
  status: string;
  totalFiles: number;
  processedFiles: number;
  errorFiles: number;
  createdAt: string;
  user: {
    username: string;
  };
}


const ManageFilesDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [operations, setOperations] = React.useState<FileOperation[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [selectedOperation, setSelectedOperation] = React.useState<FileOperation | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = React.useState<boolean>(false);

  const loadOperations = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get('/manage-files/operations', {
        params: {
          page,
          limit: 10,
          status: statusFilter || undefined,
          search: searchTerm || undefined,
        },
      });
      setOperations(response.data.data);
      setTotalPages(Math.ceil(response.data.total / 10));
    } catch (error) {
      console.error('Error loading operations:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadOperations();
  }, [page, statusFilter, searchTerm]);

  const handleViewDetails = async (operation: FileOperation) => {
    setSelectedOperation(operation);
    setDetailDialogOpen(true);
    // Load operation errors here if needed
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      pending: { color: 'default' as const, icon: <Schedule />, label: t('manageFiles.status.pending') },
      processing: { color: 'info' as const, icon: <PlayArrow />, label: t('manageFiles.status.processing') },
      completed: { color: 'success' as const, icon: <CheckCircle />, label: t('manageFiles.status.completed') },
      completed_with_errors: { color: 'warning' as const, icon: <Warning />, label: t('manageFiles.status.completedWithErrors') },
      failed: { color: 'error' as const, icon: <Error />, label: t('manageFiles.status.failed') },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        {t('manageFiles.dashboard.title')}
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {t('manageFiles.dashboard.totalOperations')}
            </Typography>
            <Typography variant="h5" component="div">
              {operations.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {t('manageFiles.dashboard.completedOperations')}
            </Typography>
            <Typography variant="h5" component="div" color="success.main">
              {operations.filter(op => op.status === 'completed').length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {t('manageFiles.dashboard.failedOperations')}
            </Typography>
            <Typography variant="h5" component="div" color="error.main">
              {operations.filter(op => op.status === 'failed').length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            label={t('manageFiles.table.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('manageFiles.table.status')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label={t('manageFiles.table.status')}
            >
              <MenuItem value="">{t('manageFiles.table.all')}</MenuItem>
              <MenuItem value="pending">{t('manageFiles.status.pending')}</MenuItem>
              <MenuItem value="processing">{t('manageFiles.status.processing')}</MenuItem>
              <MenuItem value="completed">{t('manageFiles.status.completed')}</MenuItem>
              <MenuItem value="completed_with_errors">{t('manageFiles.status.completedWithErrors')}</MenuItem>
              <MenuItem value="failed">{t('manageFiles.status.failed')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => window.location.href = '/manage-files/process'}
          >
            {t('manageFiles.dashboard.newOperation')}
          </Button>
          <IconButton onClick={loadOperations} disabled={loading}>
            <Refresh />
          </IconButton>
        </Stack>
      </Paper>

      {/* Operations Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('manageFiles.table.id')}</TableCell>
                <TableCell>{t('manageFiles.table.directoryPath')}</TableCell>
                <TableCell>{t('manageFiles.table.status')}</TableCell>
                <TableCell>{t('manageFiles.table.totalFiles')}</TableCell>
                <TableCell>{t('manageFiles.table.processedFiles')}</TableCell>
                <TableCell>{t('manageFiles.table.errorFiles')}</TableCell>
                <TableCell>{t('manageFiles.table.user')}</TableCell>
                <TableCell>{t('manageFiles.table.createdAt')}</TableCell>
                <TableCell>{t('manageFiles.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : operations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {t('manageFiles.table.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                operations.map((operation) => (
                  <TableRow key={operation.id}>
                    <TableCell>{operation.id}</TableCell>
                    <TableCell>{operation.directoryPath}</TableCell>
                    <TableCell>{getStatusChip(operation.status)}</TableCell>
                    <TableCell>{operation.totalFiles}</TableCell>
                    <TableCell>{operation.processedFiles}</TableCell>
                    <TableCell>{operation.errorFiles}</TableCell>
                    <TableCell>{operation.user.username}</TableCell>
                    <TableCell>{new Date(operation.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(operation)}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        )}
      </Paper>

      {/* Detail Dialog */}
      <OperationDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        operationId={selectedOperation?.id || 0}
        operationTitle={selectedOperation ? `${t('manageFiles.detail.title')} #${selectedOperation.id}` : ''}
      />
    </Box>
  );
};

export default ManageFilesDashboardPage;